import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Image,
  Button,
  Link,
  ScrollShadow,
} from "@heroui/react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { isProductPreviewOpen, productPreviewAtom } from "app/atoms/product.atom";
import { BsCart3 } from "react-icons/bs";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Parallax, Navigation, Pagination } from "swiper/modules";
import { Swiper as SwiperInstance } from "swiper";
import "../../style.css";
import { useEffect, useRef, useState } from "react";
import { useSafeIsMobile } from "app/utils/deviceDetection";

export function PreviewProduct() {
  const isMobile = useSafeIsMobile();
  const setProduct = useSetAtom(productPreviewAtom);
  const [isPreviewOpen, setIsProductPreviewOpen] = useAtom(isProductPreviewOpen);

  const swiperRef = useRef<SwiperInstance | null>(null);
  const product = useAtomValue(productPreviewAtom);

  const [currentImage, setCurrentImage] = useState<string>("");

  const imgRef = useRef(null);
  const [objectFit, setObjectFit] = useState("object-contain");

  const handleOnOpenChange = () => {
    setCurrentImage("");
    setProduct(null);
    setIsProductPreviewOpen(false);
  };

  useEffect(() => {
    const img = imgRef.current as unknown as HTMLImageElement;

    if (img) {
      // Check if the image overflows its container
      const isOverflowing = img.naturalHeight > img.clientHeight;
      // Set object-fit based on overflow
      setObjectFit(isOverflowing ? "object-fill" : "object-contain");
    }
  }, [currentImage, product?.image]);

  return (
    <Modal
      isOpen={isPreviewOpen}
      placement={isMobile ? "bottom-center" : "auto"}
      backdrop="opaque"
      scrollBehavior="normal"
      onClose={handleOnOpenChange}
      size="2xl"
    >
      <ModalContent className=" rounded-xl shadow-2xl border border-gray-200 dark:border-gray-500 dark:bg-gray-800">
        <>
          <ModalHeader className="justify-center">
            <div className="flex flex-col gap-1 items-center justify-center">
              <span className="text-lg md:text-xl xl:text-2xl 2xl:text-3xl font-bold">
                {product?.title}
              </span>
              <div className="flex items-center space-x-4">
                <span className="font-normal">Product Code: {product?.productCode}</span>
                {/* <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((_, i) => (
                    <FaStar key={`s-${i}`} className=" text-orange" />
                  ))}
                </div> */}
              </div>
            </div>
          </ModalHeader>
          <ModalBody className="p-1 md:p4">
            <ScrollShadow hideScrollBar size={10}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
                <div className="">
                  <Image
                    ref={imgRef}
                    width={"100%"}
                    height={"auto"}
                    alt={product?.title}
                    loading="lazy"
                    removeWrapper={true}
                    src={currentImage || product?.image}
                    classNames={{
                      img: `max-h-[60vh] border-2 border-zinc-100  w-full ${objectFit}`,
                    }}
                  />
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <span className="text-lg md:text-xl font-medium">Description</span>
                    <p className=" font-normal text-gray-700 dark:text-gray-200 line-clamp-[10]">
                      {product?.description}
                    </p>
                  </div>
                  <div className="grid grid-cols-2">
                    <Button
                      as={Link}
                      href={`/category/${product?.category}/${product?.subCatgory}/${product?.slug}`}
                      className="bg-primary text-white"
                      startContent={<BsCart3 />}
                    >
                      View Product
                    </Button>
                  </div>
                </div>
              </div>
            </ScrollShadow>
          </ModalBody>
          <ModalFooter className="justify-center border- border-red-600 p-2 md:p-4">
            <div className="p-1 md:p-4  relative w-11/12 md:w-4/5">
              <Swiper
                // navigation={true}
                onSwiper={(swiper) => (swiperRef.current = swiper)}
                navigation={{
                  nextEl: ".swiper-smallbutton-next",
                  prevEl: ".swiper-smallbutton-prev",
                  enabled: true,
                }}
                parallax={true}
                pagination={{
                  clickable: true,
                }}
                slidesPerView={1}
                spaceBetween={20}
                breakpoints={{
                  320: {
                    slidesPerView: 2.2,
                    spaceBetween: 20,
                  },
                  640: {
                    slidesPerView: 3,
                  },
                  768: {
                    slidesPerView: 4,
                  },
                  1200: {
                    slidesPerView: 4.6,
                  },
                }}
                modules={[Navigation, Pagination, Parallax, A11y]}
              >
                {product?.images &&
                  product?.images?.length > 0 &&
                  product?.images.map((image, i) => (
                    <SwiperSlide>
                      <div
                        key={`${i}_${image}`}
                        onClick={() => setCurrentImage(image)}
                        className={`h-20 cursor-pointer relative rounded-sm flex items-center ${
                          image === currentImage
                            ? "border-2 border-primary"
                            : " border-2 border-slate-500"
                        }`}
                      >
                        <Image
                          alt=""
                          radius="md"
                          src={image}
                          removeWrapper
                          width={60}
                          // className="object-cover transition aspect-square inset-0"
                          className="absolute inset-0 h-full object-cover transition-transform transform-gpu aspect-square "
                        />
                      </div>
                    </SwiperSlide>
                  ))}
              </Swiper>
              <div
                className="swiper-button-next dark:!text-white after:dark:text-white  before:!text-small after:!text-small !left-[96%] !top-2/3 after:content-['next']"
                onClick={() => swiperRef.current && swiperRef.current.slideNext()}
              ></div>
              <div
                className="swiper-button-prev dark:!text-white after:dark:text-white before:!text-small after:!text-small !left-[-4%] !top-2/3  after:content-['prev']"
                onClick={() => swiperRef.current && swiperRef.current.slidePrev()}
              ></div>
            </div>
          </ModalFooter>
        </>
      </ModalContent>
    </Modal>
  );
}
